import React, { useCallback, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { FiLogOut } from 'react-icons/fi';
import ServersPage from './pages/ServersPage';
import JobsPage from './pages/JobsPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import { NotificationProvider, useNotification } from './context/NotificationContext';
import { useWebSocket } from './hooks/useWebSocket';
import './styles/App.css';

// ─── Inner app — has access to notification context ───────────────────────────

function AppInner() {
  const { notify, notifyJobStep } = useNotification();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true' || Boolean(localStorage.getItem('authToken'));
  });
  const [userEmail, setUserEmail] = useState(() => localStorage.getItem('userEmail') || '');

  const handleWsMessage = useCallback((data) => {
    if (data.type === 'job_update') {
      // Map the current_step string or status to a step key for the toast
      console.log('handle ws msg from server page', data)
      const stepKey = data.currentStep || data.status;
      notifyJobStep(data.jobId, stepKey, data.status);
    }
  }, [notifyJobStep]);

  useWebSocket(handleWsMessage, isAuthenticated);

  const handleLogin = useCallback((response) => {
    const payload = response?.data?.data || response?.data || {};
    const token = payload?.auth?.accessToken || payload?.token || payload?.accessToken || payload?.jwt || '';
    const user = payload?.user || {};
    const email = user?.email || payload?.email || '';
    const isSuccess = response?.status === 200 || payload?.statusCode === 200 || payload?.statusCode === '200';

    if (!isSuccess) {
      notify('Login response was not successful', 'error');
      return;
    }

    if (token) {
      localStorage.setItem('authToken', token);
    }

    localStorage.setItem('authUser', JSON.stringify(user));
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('userEmail', email);
    setIsAuthenticated(true);
    setUserEmail(email);
    notify(`Signed in as ${email}`, 'success');
  }, [notify]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('authUser');
    setIsAuthenticated(false);
    setUserEmail('');
   // notify('You have been logged out', 'info');
  }, []);

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated
              ? <Navigate to="/" replace />
              : <LoginPage onLogin={handleLogin} />
          }
        />
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <div className="app">
                <nav className="navbar">
                  <div className="nav-brand">
                    <h1>Backup Deploy System</h1>
                    <p>{userEmail}</p>
                  </div>
                  <div className="nav-actions">
                    <div className="nav-links">
                      <Link to="/" className="nav-link">Servers</Link>
                      <Link to="/jobs" className="nav-link">Jobs</Link>
                      <Link to="/settings" className="nav-link">Settings</Link>
                    </div>
                    <button className="btn btn-secondary nav-logout" type="button" onClick={handleLogout}>
                      <FiLogOut />
                      <span>Logout</span>
                    </button>
                  </div>
                </nav>

                <main className="main-content">
                  <Routes>
                    <Route path="/" element={<ServersPage />} />
                    <Route path="/jobs" element={<JobsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </main>
              </div>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </Router>
  );
}

// ─── Root — provides notification context to everything ───────────────────────

function App() {
  return (
    <NotificationProvider>
      <AppInner />
    </NotificationProvider>
  );
}

export default App;
